/**
 * 📮 오른쪽 위 [개발요청] 단추 (v2.274.0 · 2026-10-18 사용자)
 *
 * 「개발요청 버튼을 메뉴 어디를 이동하든지 우측상단에 팝업처럼 버튼을 만들어주고, 누르면 팝업으로 입력창이 나와서
 *   글도 적고 파일·사진 첨부할 수 있는 형태의 작성 후 저장」
 *   · 포털 껍데기(App.tsx Shell)가 한 번만 그린다 — 화면마다 따로 넣지 않는다(새 화면이 생겨도 저절로 있다)
 *   · 누르면 게시판과 **같은 쓰는 창**(DevRequestModal) — 메뉴는 지금 보던 화면이 미리 골라져 있다
 *   · 올리면 같은 창이 그 요청 보기로 바뀌고, 「게시판에서 보기」 줄로 넘어갈 수 있다
 * v2.275.0: 자리는 모서리 묶음(`CornerActions`)이 정한다 — [💬 사용 문의] 가 왼쪽에 나란히 선다. 쓰는 창은 body 로 그린다.
 * ⚠자리(PC): 창 맨 위 가장자리에 붙은 작은 탭(위 0~24px) · 오른쪽은 본문 스크롤 막대(약 17px)를 비켜 20px.
 *   화면들은 위 여백이 24px(p-6) 또는 32px(p-8)라 그 위에 들어간다 — 처음엔 top 4px·높이 24px 이었는데
 *   메일 화면 [✉ 새 메일](위 24px)과 4px 겹쳐서 위 가장자리로 붙였다(10-18 · 메뉴마다 1280·1366·1536·1920 폭으로 재 봄 · 겹침 0).
 * ⚠자리(폰 · md 미만): 위 여백이 12px 이고 제목 줄이 메뉴 단추와 같은 높이라 오른쪽 위에 둘 데가 없다
 *   (메일 [✉ 새 메일]과 겹쳤다) → 오른쪽 아래 둥근 단추.
 */

import { lazy, Suspense, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { HelpBotFab } from './HelpBotFab';

const DevRequestModal = lazy(() =>
  import('./DevRequestBoard').then((m) => ({ default: m.DevRequestModal })),
);

export function DevRequestFab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="개발요청 — 이 화면에서 바꾸고 싶은 점을 남깁니다(글·캡처·파일). 올린 요청은 「📮 개발요청」 메뉴에 쌓입니다"
        aria-label="개발요청 쓰기"
        className={[
          'pointer-events-auto font-medium',
          // 폰: 둥근 48px(화면 0.85배라 실제 약 41px)
          'w-12 h-12 rounded-full bg-sky-600 text-white text-xl shadow-lg flex items-center justify-center',
          // PC(md 이상): 창 맨 위 가장자리에 붙은 작은 탭
          'md:w-auto md:h-6 md:px-2.5 md:rounded-none md:rounded-b',
          'md:border md:border-t-0 md:border-sky-300 md:bg-white md:text-sky-800 md:text-xs md:shadow-sm',
          'md:hover:bg-sky-50 md:hover:border-sky-400',
        ].join(' ')}
      >
        <span className="md:hidden" aria-hidden="true">📮</span>
        <span className="hidden md:inline">📮 개발요청</span>
      </button>
      {open && createPortal(
        <Suspense fallback={null}>
          <DevRequestModal
            id={null}
            showBoardLink
            onClose={() => setOpen(false)}
            onChanged={() => { void qc.invalidateQueries({ queryKey: ['dev-requests'] }); }}
          />
        </Suspense>,
        document.body,
      )}
    </>
  );
}

/**
 * 모서리 묶음 (v2.275.0) — [💬 사용 문의] [📮 개발요청] 을 한 자리에 세운다.
 *   PC: 창 맨 위 가장자리 오른쪽(20px 띄움)에 가로로 · 폰: 오른쪽 아래에 세로로(📮 가 맨 아래 — 전과 같은 자리 · 💬 는 그 위).
 *   ⚠묶음 상자는 누름을 통과시킨다(pointer-events-none) — 단추 사이 틈이 뒤 화면을 가리지 않게. 단추만 auto.
 */
export function CornerActions() {
  return (
    <div
      className={[
        // v2.319.0: PC(md 이상)는 상단 헤더(TopBar)가 같은 단추를 품는다 — 여기는 폰에서만 오른쪽 아래에 떠 있는다
        'fixed z-30 print:hidden pointer-events-none flex md:hidden',
        'bottom-4 right-4 flex-col items-end gap-3',
      ].join(' ')}
    >
      <HelpBotFab />
      <DevRequestFab />
    </div>
  );
}
